import {useEffect} from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  nextPage,
  previousPage,
  questionAnswered,
  selectAdaptabilityQuestions,
  selectPage,
  resetPage,
  selectAdaptabilityComplete
} from "../features/adaptability/adaptabilitySlice.js";
import adaptabiltyIcon from "../assets/Navigation/nav_icon_adaptability_green.png";
import SideNav from "../components/SideNav";
import PageTitle from "../components/PageTitle";
import { Link } from "react-router-dom";
import SlideOut from "../components/SlideOut.js";
import remoteMonitoringChart from '../assets/Popups/popup_remote_monitoring_graph.jpg'
import meterGraphic from '../assets/Popups/popup_meter_image.jpg'
import { ArrowBackIos, ArrowForwardIos } from "@mui/icons-material";
import { Button, TextField, Container, Grid } from "@mui/material";

const Adaptability = () => {
  const page = useSelector(selectPage);
  const questions = useSelector(selectAdaptabilityQuestions)
  const dispatch = useDispatch();
  const section = {
    title: "Adaptability & Scalability",
    icon: adaptabiltyIcon,
    pages: [1, 2, 3],
  };

  const isComplete = useSelector(selectAdaptabilityComplete)
  useEffect(
    ()=>!isComplete ? dispatch(resetPage()):null,[]
  )

  const remoteMonitoring = {
    title: 'Reduce Workforce Dependency',
    copy: 'Roxar 2600 Multiphase Flow Meters provide remote access data to inform workforce dependencies as your operations fluctuates:',
    bullets: [<li>Reduced dependency on workforce additions/reductions as operation grows/shrinks </li>],
    view:{
      vert:true,
      copy:['View software:',<br/>,'Remote monitoring'],
      paragraph:['Reduce workforce dependency by leveraging the remote access capabilities and operational insight delivered by the Roxar 2600 Multiphase Flow Meter.'],
      graphic: remoteMonitoringChart
    }
  }

  const meterConfiguration = {
    title: 'Adaptable Meter Configuration',
    copy:'Mechanical as well as software configuration for the Roxar 2600 Multiphase Flow Meter is field adaptable:',
    bullets: [<li>Automized and continuous software configuration adapting to rapidly changing flow regimes.</li>,<li>Ability to swap venturi in field extends the flow rate operating envelope of the meter.</li>],
    view:{
      vert:false,
      copy:['Learn more:',<br/>,'Adaptable Meter', <br />, 'Configuration'],
      paragraph:['With the agile Rapid Adaptive Measurement software architecture, the Roxar 2600 MPFM automatically selects the optimal calculation methods according to the flow profile the meter observes at any given time.',<br />,<br />,' The replaceable venturi design allows for in-field change-out to a narrower venturi throat which extends the operating envelope as flow rates typically decline throughout lifetime of the well.'],
      graphic: meterGraphic
    }
  }

  return (
    <Container className="content_container" maxWidth='lg'>
      <div className="roxar_content">
      <div className="main_content">
        <PageTitle section={section} currentPage={page} />
        <div className="page_contents">
          {page === 1 && (
            <div className="challenges">
              <div>
                <h1>Production Challenges</h1>
                <ul>
                  <li>
                    Practical limitation on number of wells/visits per field
                    technician when physical presence required to identify,
                    troubleshoot, and remedy issues
                  </li>
                  <li>
                    Separator design and sizing limited to certain flow
                    ranges/production profiles, requiring major overhauls in
                    changing production environments.
                  </li>
                </ul>
              </div>
              <div className="solution_copy">
                <p>
                  Optimizing production is the ultimate goal of any operation
                  but practical limits like physical presence of field
                  technicians to identify, troubleshoot, and remedy issues
                  provide limitations. Separator design and sizing can also
                  limit production, requiring major overhauls to change
                  production environments.
                </p>
                <Button className="forward_button production_button" onClick={() => dispatch(nextPage())}>Learn How</Button>
              </div>
            </div>
          )}
          {page === 2 && (
            <div className="questions">
                  <h3 className="question">Q: How frequently do your field engineers visit each well pad?</h3>
                  <div className="answers">
                      <Button className={`answer ${questions[0].answer==='Every 1-2 weeks'?'active_answer':null}`} onClick={(e)=> dispatch(questionAnswered({question:0,answer:'Every 1-2 weeks', score:1, max: 4}))} > Every 1-2 weeks</Button>
                      <Button className={`answer ${questions[0].answer==='Every 3-4 weeks'?'active_answer':null}`} onClick={(e)=> dispatch(questionAnswered({question:0,answer:'Every 3-4 weeks', score:2, max: 4}))}> Every 3-4 weeks</Button>
                      <Button className={`answer ${questions[0].answer==='Every 5-6 weeks'?'active_answer':null}`} onClick={(e)=> dispatch(questionAnswered({question:0,answer:'Every 5-6 weeks', score:3, max: 4}))}> Every 5-6 weeks</Button>
                      <Button className={`answer ${questions[0].answer==='Every 7+ weeks'?'active_answer':null}`} onClick={(e)=> dispatch(questionAnswered({question:0,answer:'Every 7+ weeks', score:4, max: 4}))}> Every 7+ weeks</Button>
                  </div>
            
                  <h3 className="question">Q: What is the ideal number of wells each field engineer can effectively manage?</h3>
                  <div className="answers short_answers">
                      <Button className={`answer ${questions[1].answer==='1'?'active_answer':null}`} onClick={(e)=> dispatch(questionAnswered({question:1,answer:'1', score:1, max: 5}))}>1</Button>
                      <Button className={`answer ${questions[1].answer==='2-5'?'active_answer':null}`} onClick={(e)=> dispatch(questionAnswered({question:1,answer:'2-5', score:2, max: 5}))}> 2-5</Button>
                      <Button className={`answer ${questions[1].answer==='6-10'?'active_answer':null}`} onClick={(e)=> dispatch(questionAnswered({question:1,answer:'6-10', score:3, max: 5}))}> 6-10</Button>
                <div></div>
                
                      <Button className={`answer ${questions[1].answer==='11-50'?'active_answer':null}`} onClick={(e)=> dispatch(questionAnswered({question:1,answer:'11-50', score:4, max: 5}))}> 11-50</Button>
                      <Button className={`answer ${questions[1].answer==='50+'?'active_answer':null}`} onClick={(e)=> dispatch(questionAnswered({question:1,answer:'50+', score:5, max: 5}))}> 50+</Button>
                  </div>
              
              <div className="page_nav">
                  <Button className="prev_button" onClick={() => dispatch(previousPage())}><ArrowBackIos /><span>Previous</span></Button>
                  <Button className="next_button" onClick={() => dispatch(nextPage())}><span>Next</span><ArrowForwardIos /></Button>
              </div>
            </div>
          )}
          {page === 3 && (
            <div className="questions">
             
                  <h3 className="question">Q: Have you ever had to retrofit/swap out wellsite equipment due to changes in production profiles/flow rates/etc?</h3>
                  <div className="answers">
                      <Button className={`answer ${questions[2].answer==='Yes'?'active_answer':null}`} onClick={(e)=> dispatch(questionAnswered({question:2,answer:'Yes', score:2, max: 4}))}>Yes</Button>
                      <Button className={`answer ${questions[2].answer==='No'?'active_answer':null}`} onClick={(e)=> dispatch(questionAnswered({question:2,answer:'No', score:2, max: 4}))} >No</Button>
                  </div>
           
              
                  <div className="page_nav">
              <Button className="prev_button" onClick={() => dispatch(previousPage())}><ArrowBackIos /><span>Previous</span></Button>
                  <Button className="next_button"  component={Link} to={'/data'}><span>Next</span><ArrowForwardIos /></Button>
              </div>
              <div>
                  <Button component={Link} to='/results' className="next_button view_results">View Section Results</Button>
              </div>
            </div>
          )}
        </div>
      </div>
        {page === 2 && (
          <SlideOut title={remoteMonitoring.title} copy={remoteMonitoring.copy}  bullets={remoteMonitoring.bullets} view={remoteMonitoring.view}/>
          )}
          {page === 3 && (
            <SlideOut title={meterConfiguration.title} copy={meterConfiguration.copy}  bullets={meterConfiguration.bullets} view={meterConfiguration.view}/>
            )}
      <SideNav />
      </div>
    </Container>
  );
};

export default Adaptability;
